import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#00ff88', // Vibrant green from your design
      light: '#66ffb3',
      dark: '#00cc6d',
      contrastText: '#000814',
    },
    secondary: {
      main: '#0099ff', // Bright blue from your design
      light: '#4db8ff',
      dark: '#0077cc',
      contrastText: '#000814',
    },
    tertiary: {
      main: '#ff3366', // Pink/red accent from your design
      light: '#ff668c',
      dark: '#cc2952',
      contrastText: '#ffffff',
    },
    success: {
      main: '#00ff88',
      light: '#66ffb3',
      dark: '#00cc6d',
      contrastText: '#000814',
    },
    error: {
      main: '#ff3366',
      light: '#ff668c',
      dark: '#cc2952',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#ffcc00', // Yellow/gold from your design
      light: '#ffd633',
      dark: '#cca300',
      contrastText: '#000814',
    },
    info: {
      main: '#0099ff',
      light: '#4db8ff',
      dark: '#0077cc',
      contrastText: '#000814',
    },
    background: {
      default: '#000814', // Deep space blue
      paper: '#001d3d', // Dark blue
    },
    gradient: {
      primary: 'linear-gradient(135deg, #000814 0%, #001d3d 50%, #003566 100%)',
      secondary: 'linear-gradient(45deg, #00ff88, #0099ff)',
      accent: 'linear-gradient(45deg, #ff3366, #ffcc00)',
      glass: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
    },
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.8)',
      disabled: 'rgba(255, 255, 255, 0.5)',
      gradient: 'linear-gradient(45deg, #00ff88, #0099ff)',
    },
  },
  typography: {
    fontFamily: [
      '"Inter"',
      '"SF Pro Display"',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontWeight: 800,
      fontSize: '3.5rem',
      lineHeight: 1.1,
      letterSpacing: '-0.02em',
      background: 'linear-gradient(45deg, #00ff88, #0099ff)',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    },
    h2: {
      fontWeight: 700,
      fontSize: '2.75rem',
      lineHeight: 1.2,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontWeight: 700,
      fontSize: '2.25rem',
      lineHeight: 1.3,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.75rem',
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.4,
    },
    subtitle1: {
      fontWeight: 500,
      fontSize: '1.125rem',
      lineHeight: 1.6,
    },
    subtitle2: {
      fontWeight: 500,
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.7,
      fontWeight: 400,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
      fontWeight: 400,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.9375rem',
      letterSpacing: '0.025em',
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.5,
      fontWeight: 400,
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },
  },
  shape: {
    borderRadius: 12,
  },
  spacing: 8,
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollBehavior: 'smooth',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#001d3d',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'linear-gradient(45deg, #00ff88, #0099ff)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'linear-gradient(45deg, #00cc6d, #0077cc)',
          },
        },
        '@global': {
          '@keyframes gradientShift': {
            '0%': { backgroundPosition: '0% 50%' },
            '50%': { backgroundPosition: '100% 50%' },
            '100%': { backgroundPosition: '0% 50%' },
          },
          '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0px)' },
            '50%': { transform: 'translateY(-10px)' },
          },
          '@keyframes glow': {
            '0%, 100%': { boxShadow: '0 0 20px rgba(0, 255, 136, 0.3)' },
            '50%': { boxShadow: '0 0 30px rgba(0, 255, 136, 0.6)' },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          padding: '12px 32px',
          borderRadius: '50px',
          fontWeight: 600,
          fontSize: '0.9375rem',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, transparent, rgba(255,255,255,0.1), transparent)',
            transform: 'translateX(-100%)',
            transition: 'transform 0.6s ease',
          },
          '&:hover::before': {
            transform: 'translateX(100%)',
          },
        },
        contained: {
          background: 'linear-gradient(45deg, #00ff88, #0099ff)',
          color: '#000814',
          boxShadow: '0 8px 25px rgba(0, 255, 136, 0.3)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 12px 35px rgba(0, 255, 136, 0.4)',
            background: 'linear-gradient(45deg, #00cc6d, #0077cc)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
        outlined: {
          border: '2px solid',
          borderColor: '#00ff88',
          color: '#00ff88',
          background: 'transparent',
          '&:hover': {
            background: 'rgba(0, 255, 136, 0.1)',
            borderColor: '#00ff88',
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(0, 255, 136, 0.2)',
          },
        },
        text: {
          color: '#00ff88',
          '&:hover': {
            background: 'rgba(0, 255, 136, 0.1)',
            transform: 'translateY(-1px)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '20px',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: 'linear-gradient(90deg, #00ff88, #0099ff)',
            transform: 'scaleX(0)',
            transition: 'transform 0.4s ease',
          },
          '&:hover': {
            transform: 'translateY(-8px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
            '&::before': {
              transform: 'scaleX(1)',
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, rgba(0, 8, 20, 0.95) 0%, rgba(0, 29, 61, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            background: 'transparent',
            backgroundColor: 'transparent',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            transition: 'all 0.3s ease',
            '&:hover': {
              border: '1px solid rgba(255, 255, 255, 0.2)',
              background: 'transparent',
              backgroundColor: 'transparent',
            },
            '&.Mui-focused': {
              border: '1px solid #00ff88',
              boxShadow: '0 0 0 2px rgba(0, 255, 136, 0.1)',
              background: 'transparent',
              backgroundColor: 'transparent',
            },
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '1rem',
            marginBottom: '8px',
          },
          '& .MuiFormLabel-root': {
            fontSize: '1rem',
          },
          '& .MuiInputBase-input': {
            color: '#ffffff',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          fontWeight: 500,
          background: 'rgba(255, 255, 255, 0.1)',
          color: 'rgba(255, 255, 255, 0.9)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          '&:hover': {
            background: 'rgba(255, 255, 255, 0.15)',
          },
        },
        filled: {
          background: 'linear-gradient(45deg, #00ff88, #0099ff)',
          color: '#000814',
          fontWeight: 600,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#00ff88',
          textDecoration: 'none',
          fontWeight: 500,
          transition: 'all 0.3s ease',
          '&:hover': {
            color: '#66ffb3',
            textDecoration: 'underline',
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(255, 255, 255, 0.1)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(90deg, rgba(255,255,255,0.1) 25%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0.1) 75%)',
          backgroundSize: '200% 100%',
          animation: 'loading 1.5s infinite',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'all 0.3s ease',
          '&:hover': {
            background: 'rgba(255, 255, 255, 0.1)',
            transform: 'scale(1.1)',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          // Remove the line that appears below the label (top border of the outline)
          borderTop: 'none',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
        },
        outlined: {
          '&.MuiInputLabel-shrink': {
            marginBottom: '8px',
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          marginTop: 0,
          marginBottom: '20px',
        },
      },
    },
  },
  custom: {
    gradients: {
      primary: 'linear-gradient(135deg, #000814 0%, #001d3d 50%, #003566 100%)',
      secondary: 'linear-gradient(45deg, #00ff88, #0099ff)',
      accent: 'linear-gradient(45deg, #ff3366, #ffcc00)',
      glass: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
    },
    animations: {
      gradientShift: 'gradientShift 3s ease infinite',
      float: 'float 3s ease-in-out infinite',
      glow: 'glow 2s ease-in-out infinite',
    },
    shadows: {
      glow: '0 0 20px rgba(0, 255, 136, 0.3)',
      premium: '0 20px 40px rgba(0, 0, 0, 0.4)',
    },
  },
});

// Add custom variants
theme.components.MuiButton.variants = [
  {
    props: { variant: 'gradient' },
    style: {
      background: theme.palette.gradient.secondary,
      color: '#000814',
      '&:hover': {
        background: 'linear-gradient(45deg, #00cc6d, #0077cc)',
      },
    },
  },
  {
    props: { variant: 'glass' },
    style: {
      background: theme.palette.gradient.glass,
      backdropFilter: 'blur(20px)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      color: '#ffffff',
      '&:hover': {
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.1))',
        border: '1px solid rgba(255, 255, 255, 0.2)',
      },
    },
  },
];

export default theme;